package se.mittpass.web.components

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import se.mittpass.shared.api.SlotDto
import se.mittpass.shared.api.WorkplaceDto
import se.mittpass.shared.api.calculateDefaultBreakMinutes

// ──────── SlotModal: create / edit ────────

@Composable
internal fun SlotModal(
    title: String,
    subtitle: String,
    day: String,
    startTime: String,
    endTime: String,
    allowDayEdit: Boolean,
    confirmLabel: String,
    showDelete: Boolean = false,
    staff: List<se.mittpass.shared.api.StaffMemberDto> = emptyList(),
    absences: List<se.mittpass.shared.api.AbsenceRequestDto> = emptyList(),
    initialAssignedUserId: Long? = null,
    initialBreakMinutes: Int? = null,
    onConfirm: (day: String, start: String, end: String, breakMin: Int, notify: Boolean, assignedUserId: Long?) -> Unit,
    onDelete: (() -> Unit)? = null,
    onCancel: () -> Unit
) {
    var d by remember(day) { mutableStateOf(day) }
    var s by remember(startTime) { mutableStateOf(normalizeTime(startTime)) }
    var e by remember(endTime) { mutableStateOf(normalizeTime(endTime)) }

    val initialDuration = calculateDurationMinutes(s, e)
    val autoBreak = se.mittpass.shared.api.calculateDefaultBreakMinutes(initialDuration)
    var breakMin by remember(initialBreakMinutes) { mutableStateOf(initialBreakMinutes ?: autoBreak) }
    var userModifiedBreak by remember { mutableStateOf(initialBreakMinutes != null) }

    var notify by remember { mutableStateOf(true) }
    var assignedUserId by remember(initialAssignedUserId) { mutableStateOf(initialAssignedUserId) }
    var saving by remember { mutableStateOf(false) }
    val modalOpenTime = remember { kotlinx.browser.window.performance.now() }

    Div({
        onClick {
            val now = kotlinx.browser.window.performance.now()
            if (now - modalOpenTime > 400.0) {
                onCancel()
            }
        }
        style {
            property("position", "fixed"); property("inset", "0")
            property("background", "rgba(13,13,24,0.4)"); property("z-index", "100")
            property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
            property("backdrop-filter", "blur(3px)"); property("padding", "1rem")
        }
    }) {
        Div({
            onClick { it.stopPropagation() }
            style {
                property("background", "var(--rp-surface)"); property("border-radius", "20px")
                property("padding", "1.75rem 1.85rem"); property("width", "440px")
                property("max-width", "100%"); property("box-shadow", "0 24px 60px rgba(13,13,24,0.25)")
            }
        }) {
            H3({ style {
                property("margin", "0 0 0.25rem"); property("font-size", "1.15rem")
                property("font-weight", "700"); property("color", "var(--rp-text)")
            } }) { Text(title) }
            P({ style {
                property("margin", "0 0 1.4rem"); property("color", "var(--rp-muted)"); property("font-size", "0.875rem")
            } }) { Text(subtitle) }

            var presetsList by remember { mutableStateOf(loadPresetsFromStorage()) }
            var isAddingPreset by remember { mutableStateOf(false) }
            var newPresetName by remember { mutableStateOf("") }

            Div({ style { property("margin-bottom", "1.1rem") } }) {
                Div({ style {
                    property("display", "flex"); property("justify-content", "space-between"); property("align-items", "center")
                    property("margin-bottom", "0.4rem")
                } }) {
                    Span({ style { schemaModalLabel() } }) { Text("Snabbval för pass") }
                    Button({
                        onClick { isAddingPreset = !isAddingPreset }
                        style {
                            property("background", "transparent"); property("border", "none")
                            property("color", "var(--rp-pine)"); property("font-size", "0.78rem")
                            property("font-weight", "700"); property("cursor", "pointer")
                        }
                    }) {
                        Text(if (isAddingPreset) "Avbryt" else "+ Spara nuvarande som snabbval")
                    }
                }

                if (isAddingPreset) {
                    Div({ style {
                        property("display", "flex"); property("gap", "0.4rem"); property("margin-bottom", "0.6rem")
                        property("background", "var(--rp-info-bg)"); property("padding", "0.5rem"); property("border-radius", "8px")
                    } }) {
                        Input(InputType.Text) {
                            value(newPresetName)
                            onInput { newPresetName = it.value }
                            placeholder("Passnamn (t.ex. Helgpass)")
                            style {
                                property("flex", "1"); property("padding", "0.35rem 0.6rem")
                                property("border-radius", "6px"); property("border", "1px solid var(--rp-info-border)")
                                property("font-size", "0.8rem")
                            }
                        }
                        Button({
                            onClick {
                                if (newPresetName.isNotBlank()) {
                                    val newP = ShiftPreset(newPresetName.trim(), s, e, breakMinutes = breakMin, isCustom = true)
                                    val updated = presetsList + newP
                                    presetsList = updated
                                    saveCustomPresetsToStorage(updated)
                                    newPresetName = ""
                                    isAddingPreset = false
                                }
                            }
                            style {
                                property("background", "var(--rp-pine)"); property("color", "var(--rp-surface)")
                                property("border", "none"); property("border-radius", "6px")
                                property("padding", "0.35rem 0.75rem"); property("font-weight", "700")
                                property("font-size", "0.78rem"); property("cursor", "pointer")
                            }
                        }) { Text("Spara") }
                    }
                }

                Div({ style {
                    property("display", "grid"); property("grid-template-columns", "1fr 1fr"); property("gap", "0.45rem")
                } }) {
                    presetsList.forEach { preset ->
                        val isSelected = s == preset.start && e == preset.end
                        Button({
                            onClick {
                                s = preset.start
                                e = preset.end
                                breakMin = preset.breakMinutes
                                userModifiedBreak = true
                            }
                            style {
                                property("padding", "0.45rem 0.65rem")
                                property("border-radius", "8px")
                                property("border", if (isSelected) "1.5px solid var(--rp-pine)" else "1px solid var(--rp-hl-med)")
                                property("background", if (isSelected) "var(--rp-info-bg)" else "var(--rp-surface)")
                                property("color", if (isSelected) "var(--rp-pine)" else "var(--rp-subtle)")
                                property("font-size", "0.78rem")
                                property("font-weight", if (isSelected) "700" else "600")
                                property("cursor", "pointer")
                                property("display", "flex"); property("justify-content", "space-between"); property("align-items", "center")
                            }
                        }) {
                            Span({ style { property("overflow", "hidden"); property("text-overflow", "ellipsis"); property("white-space", "nowrap") } }) {
                                Text(preset.name)
                            }
                            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.3rem") } }) {
                                Span({ style { property("font-size", "0.72rem"); property("color", if (isSelected) "var(--rp-pine)" else "var(--rp-muted)") } }) {
                                    Text("${preset.start}–${preset.end}")
                                }
                                if (preset.isCustom) {
                                    Span({
                                        onClick { evt ->
                                            evt.stopPropagation()
                                            val updated = presetsList.filter { it != preset }
                                            presetsList = updated
                                            saveCustomPresetsToStorage(updated)
                                        }
                                        style { property("color", "var(--rp-err-text)"); property("padding", "0 0.15rem") }
                                    }) {
                                        FaIcon("fa-solid fa-xmark", size = "0.75rem", color = "var(--rp-err-text)")
                                    }
                                }
                            }
                        }
                    }
                }
            }

            Div({ style { property("margin-bottom", "0.85rem") } }) {
                Span({ style { schemaModalLabel() } }) { Text("Dag") }
                if (allowDayEdit) {
                    Input(InputType.Date) {
                        value(d); onInput { d = it.value }
                        style { schemaModalInput() }
                    }
                } else {
                    Div({ style {
                        property("padding", "0.7rem 0.85rem"); property("background", "var(--rp-info-bg)")
                        property("border-radius", "10px"); property("font-weight", "600"); property("color", "var(--rp-pine)")
                    } }) { Text(d) }
                }
            }

            // Material 3 24-Hour Time Pickers (No Native Scroll Wheel!)
            Div({ style {
                property("display", "grid"); property("grid-template-columns", "1fr 1fr"); property("gap", "0.85rem"); property("margin-bottom", "0.6rem")
            } }) {
                Material3TimePicker24h(label = "Starttid", timeValue = s, onTimeChange = { s = it })
                Material3TimePicker24h(label = "Sluttid", timeValue = e, onTimeChange = { e = it })
            }

            // Quick Duration Pills
            Div({ style {
                property("display", "flex"); property("align-items", "center"); property("gap", "0.35rem"); property("margin-bottom", "0.85rem")
            } }) {
                Span({ style { property("font-size", "0.72rem"); property("color", "var(--rp-subtle)"); property("font-weight", "600"); property("margin-right", "0.2rem") } }) {
                    Text("Sätt längd:")
                }
                listOf(4, 8, 9).forEach { hrs ->
                    Button({
                        onClick { e = addHoursToTime(s, hrs) }
                        style {
                            property("padding", "0.25rem 0.55rem")
                            property("border-radius", "6px")
                            property("border", "1px solid var(--rp-hl-med)")
                            property("background", "var(--rp-surface)")
                            property("color", "var(--rp-pine)")
                            property("font-size", "0.72rem")
                            property("font-weight", "700")
                            property("cursor", "pointer")
                        }
                    }) {
                        Text("+$hrs tim")
                    }
                }
            }

            val durationMin = calculateDurationMinutes(s, e)
            val isValidTime = durationMin > 0

            // If user has not manually modified break, keep break in sync with default formula
            if (!userModifiedBreak && isValidTime) {
                breakMin = se.mittpass.shared.api.calculateDefaultBreakMinutes(durationMin)
            }

            // Live Duration Badge & Validation Alert
            Div({ style {
                property("background", if (isValidTime) "var(--rp-ok-bg)" else "var(--rp-err-bg)")
                property("border", if (isValidTime) "1px solid var(--rp-ok-border)" else "1px solid var(--rp-err-border)")
                property("border-radius", "10px")
                property("padding", "0.55rem 0.85rem")
                property("margin-bottom", "0.85rem")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "space-between")
            } }) {
                if (isValidTime) {
                    val netM = (durationMin - breakMin).coerceAtLeast(0)
                    Span({ style { property("color", "var(--rp-ok-text)"); property("font-size", "0.82rem"); property("font-weight", "700") } }) {
                        Text("Passlängd: ${formatDuration(durationMin)}" + if (breakMin > 0) " (Effektiv tid: ${formatDuration(netM)})" else "")
                    }
                    Span({ style { property("color", "var(--rp-ok-text)"); property("font-size", "0.75rem"); property("font-weight", "600") } }) {
                        Text("OK")
                    }
                } else {
                    Span({ style { property("color", "var(--rp-err-text)"); property("font-size", "0.82rem"); property("font-weight", "700") } }) {
                        Text("Sluttiden måste vara efter starttiden.")
                    }
                }
            }

            // Break / Rast selector
            Div({ style { property("margin-bottom", "1.1rem") } }) {
                Div({ style {
                    property("display", "flex"); property("justify-content", "space-between"); property("align-items", "center")
                    property("margin-bottom", "0.35rem")
                } }) {
                    Span({ style { schemaModalLabel() } }) { Text("Rast") }
                    Span({ style { property("font-size", "0.74rem"); property("color", "var(--rp-subtle)"); property("font-weight", "600") } }) {
                        Text(if (breakMin > 0) "$breakMin min obetald rast" else "Ingen rast")
                    }
                }

                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.45rem") } }) {
                    listOf(0 to "0 min", 30 to "30 min", 45 to "45 min", 60 to "1 tim").forEach { (bm, label) ->
                        val isSelected = breakMin == bm
                        Button({
                            onClick {
                                breakMin = bm
                                userModifiedBreak = true
                            }
                            style {
                                property("flex", "1")
                                property("padding", "0.4rem 0.5rem")
                                property("border-radius", "8px")
                                property("border", if (isSelected) "1.5px solid var(--rp-pine)" else "1px solid var(--rp-hl-med)")
                                property("background", if (isSelected) "var(--rp-info-bg)" else "var(--rp-surface)")
                                property("color", if (isSelected) "var(--rp-pine)" else "var(--rp-text)")
                                property("font-size", "0.78rem")
                                property("font-weight", if (isSelected) "700" else "600")
                                property("cursor", "pointer")
                                property("text-align", "center")
                            }
                        }) {
                            Text(label)
                        }
                    }

                    // Custom break minutes input
                    Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.25rem"); property("width", "75px") } }) {
                        Input(type = InputType.Number) {
                            value(breakMin.toString())
                            onInput { ev ->
                                val num = ev.value as? Number
                                val v = num?.toInt() ?: ev.value.toString().toIntOrNull() ?: 0
                                breakMin = v.coerceIn(0, 360)
                                userModifiedBreak = true
                            }
                            style {
                                property("width", "100%")
                                property("padding", "0.35rem 0.4rem")
                                property("border-radius", "8px")
                                property("border", "1px solid var(--rp-hl-med)")
                                property("font-size", "0.78rem")
                                property("font-weight", "700")
                                property("text-align", "center")
                            }
                        }
                    }
                }
            }

                Label(attrs = {
                    style {
                        property("display", "flex"); property("align-items", "center"); property("gap", "0.55rem")
                        property("font-weight", "600"); property("color", "var(--rp-text)"); property("cursor", "pointer")
                        property("font-size", "0.9rem")
                    }
                }) {
                    Input(InputType.Checkbox) {
                        checked(notify); onInput { notify = it.value }
                        style {
                            property("width", "18px"); property("height", "18px")
                            property("accent-color", "var(--rp-pine)")
                        }
                    }
                    Text("Meddela personal")
                }

            if (staff.isNotEmpty()) {
                Div({ style { property("margin-bottom", "1.5rem") } }) {
                    Span({ style { schemaModalLabel() } }) { Text("Tilldelad personal") }
                    Select({
                        attr("value", assignedUserId?.toString() ?: "")
                        onChange { e -> assignedUserId = e.target.value.toLongOrNull() }
                        style { schemaModalInput() }
                    }) {
                        Option(value = "", attrs = {
                            if (assignedUserId == null) selected()
                        }) {
                            Text("Ingen vald (Otilldelat pass)")
                        }
                        staff.forEach { st ->
                            val isSelected = st.userId == assignedUserId
                            val userAbsence = absences.firstOrNull { vac ->
                                vac.userId == st.userId && (vac.status == "approved" || vac.status == "pending") &&
                                vac.startDate <= d && vac.endDate >= d
                            }
                            val tag = when (userAbsence?.type) {
                                "vacation" -> " [Semester]"
                                "sick" -> " [Sjuk]"
                                "vab" -> " [VAB]"
                                "leave" -> " [Tjänstledig]"
                                else -> ""
                            }
                            val name = listOfNotNull(st.firstName, st.lastName).joinToString(" ").ifEmpty { st.email }
                            Option(value = st.userId.toString(), attrs = {
                                if (isSelected) selected()
                            }) {
                                Text("$name$tag")
                            }
                        }
                    }

                    val currentAbsence = absences.firstOrNull { vac ->
                        vac.userId == assignedUserId && (vac.status == "approved" || vac.status == "pending") &&
                        vac.startDate <= d && vac.endDate >= d
                    }
                    if (currentAbsence != null) {
                        val typeLabel = when (currentAbsence.type) {
                            "vacation" -> "Semester"
                            "sick" -> "Sjukfrånvaro"
                            "vab" -> "VAB"
                            else -> "Frånvaro"
                        }
                        Div({ style {
                            property("background", "var(--rp-warn-bg)"); property("color", "var(--rp-gold)")
                            property("padding", "0.65rem 0.85rem"); property("border-radius", "10px")
                            property("font-size", "0.825rem"); property("font-weight", "600")
                            property("margin-top", "0.5rem"); property("border", "1px solid var(--rp-warn-border)")
                            property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem")
                        } }) {
                            FaIcon("fa-solid fa-triangle-exclamation", size = "0.85rem", color = "var(--rp-warn-text)")
                            Span { Text("Märk: Den anställde har registrerad $typeLabel (${currentAbsence.startDate} – ${currentAbsence.endDate}).") }
                        }
                    }
                }
            }

            Div({ style {
                property("display", "flex"); property("gap", "0.6rem"); property("justify-content", "space-between"); property("align-items", "center")
            } }) {
                Div {
                    if (showDelete && onDelete != null) {
                        Button({
                            onClick { onDelete() }
                            style {
                                property("background", "transparent"); property("color", "var(--rp-love)")
                                property("border", "1px solid var(--rp-err-border)"); property("border-radius", "10px")
                                property("padding", "0.6rem 1rem"); property("cursor", "pointer")
                                property("font-weight", "600"); property("font-size", "0.875rem")
                                property("font-family", "inherit")
                            }
                        }) { Text("Ta bort") }
                    }
                }
                Div({ style { property("display", "flex"); property("gap", "0.55rem") } }) {
                    Button({
                        onClick { onCancel() }
                        style {
                            property("background", "transparent"); property("color", "var(--rp-subtle)")
                            property("border", "1px solid var(--rp-hl-med)"); property("border-radius", "10px")
                            property("padding", "0.65rem 1.1rem"); property("cursor", "pointer")
                            property("font-weight", "600"); property("font-size", "0.9rem")
                            property("font-family", "inherit")
                        }
                    }) { Text("Avbryt") }
                    Button({
                        onClick { saving = true; onConfirm(d, s, e, breakMin, notify, assignedUserId) }
                        if (saving || !isValidTime) disabled()
                        style {
                            property("background", "var(--rp-pine)"); property("color", "var(--rp-surface)")
                            property("border", "none"); property("border-radius", "10px")
                            property("padding", "0.65rem 1.3rem"); property("cursor", "pointer")
                            property("font-weight", "700"); property("font-size", "0.9rem")
                            property("font-family", "inherit")
                            if (saving || !isValidTime) property("opacity", "0.5")
                        }
                    }) { Text(if (saving) "Sparar…" else confirmLabel) }
                }
            }
        }
    }
}

private fun normalizeTime(t: String): String {
    val parts = t.split(":")
    return "${parts[0].padStart(2, '0')}:${(parts.getOrNull(1) ?: "00").padStart(2, '0')}"
}

private fun calculateDurationMinutes(startTime: String, endTime: String): Int {
    val startParts = startTime.split(":").mapNotNull { it.toIntOrNull() }
    val endParts = endTime.split(":").mapNotNull { it.toIntOrNull() }
    if (startParts.size < 2 || endParts.size < 2) return 0
    val startM = startParts[0] * 60 + startParts[1]
    val endM = endParts[0] * 60 + endParts[1]
    return endM - startM
}

private fun formatDuration(minutes: Int): String {
    if (minutes <= 0) return "Ogiltig tid"
    val h = minutes / 60
    val m = minutes % 60
    return when {
        h > 0 && m > 0 -> "$h tim $m min"
        h > 0 -> "$h timmar"
        else -> "$m minuter"
    }
}

private fun addHoursToTime(time: String, hours: Int): String {
    val parts = time.split(":").mapNotNull { it.toIntOrNull() }
    if (parts.size < 2) return time
    val newM = (parts[0] * 60 + parts[1] + hours * 60).coerceIn(0, 1439)
    val h = newM / 60
    val m = newM % 60
    return "${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}"
}

private fun adjustMinutes(time: String, delta: Int): String {
    val parts = time.split(":").mapNotNull { it.toIntOrNull() }
    if (parts.isEmpty()) return "08:00"
    val h = parts[0]
    val m = parts.getOrNull(1) ?: 0
    val totalM = (h * 60 + m + delta).coerceIn(0, 1439)
    val newH = totalM / 60
    val newM = totalM % 60
    return "${newH.toString().padStart(2, '0')}:${newM.toString().padStart(2, '0')}"
}

private data class ShiftPreset(
    val name: String,
    val start: String,
    val end: String,
    val breakMinutes: Int = 0,
    val isCustom: Boolean = false
)

private fun loadPresetsFromStorage(): List<ShiftPreset> {
    val defaults = listOf(
        ShiftPreset("Morgon", "08:00", "16:00", breakMinutes = 60),
        ShiftPreset("Dag", "10:00", "18:00", breakMinutes = 60),
        ShiftPreset("Eftermiddag", "12:00", "20:00", breakMinutes = 60),
        ShiftPreset("Kväll / Stäng", "14:00", "22:00", breakMinutes = 60)
    )
    val raw = kotlinx.browser.window.localStorage.getItem("mittpass_custom_presets") ?: return defaults
    return try {
        val items = raw.split(";").filter { it.isNotBlank() }.mapNotNull { entry ->
            val parts = entry.split("|")
            if (parts.size >= 3) {
                val bMin = parts.getOrNull(3)?.toIntOrNull() ?: calculateDefaultBreakMinutes(calculateDurationMinutes(parts[1], parts[2]))
                ShiftPreset(parts[0], parts[1], parts[2], breakMinutes = bMin, isCustom = true)
            } else null
        }
        defaults + items
    } catch (_: Throwable) {
        defaults
    }
}

private fun saveCustomPresetsToStorage(customPresets: List<ShiftPreset>) {
    val raw = customPresets.filter { it.isCustom }.joinToString(";") { "${it.name}|${it.start}|${it.end}|${it.breakMinutes}" }
    kotlinx.browser.window.localStorage.setItem("mittpass_custom_presets", raw)
}

@Composable
private fun Material3TimePicker24h(
    label: String,
    timeValue: String,
    onTimeChange: (String) -> Unit
) {
    var isPickerOpen by remember { mutableStateOf(false) }
    var activeTab by remember { mutableStateOf("HOUR") }
    var isTextInputMode by remember { mutableStateOf(false) }

    val parts = timeValue.split(":")
    val currentH = (parts.getOrNull(0)?.toIntOrNull() ?: 8).coerceIn(0, 23)
    val currentM = (parts.getOrNull(1)?.toIntOrNull() ?: 0).coerceIn(0, 59)

    Div({ style { property("position", "relative") } }) {
        Span({ style { schemaModalLabel() } }) { Text(label) }

        Button({
            onClick { isPickerOpen = !isPickerOpen }
            style {
                property("width", "100%")
                property("padding", "0.55rem 0.85rem")
                property("border-radius", "10px")
                property("border", if (isPickerOpen) "2px solid var(--rp-iris)" else "1px solid var(--rp-hl-med)")
                property("background", if (isPickerOpen) "var(--rp-info-bg)" else "var(--rp-surface)")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "space-between")
                property("cursor", "pointer")
                property("font-family", "inherit")
            }
        }) {
            Span({
                style {
                    property("font-size", "1.05rem")
                    property("font-weight", "800")
                    property("color", "var(--rp-text)")
                    property("letter-spacing", "0.5px")
                }
            }) {
                Text("${currentH.toString().padStart(2, '0')}:${currentM.toString().padStart(2, '0')}")
            }
            FaIcon("fa-regular fa-clock", size = "0.9rem", color = "var(--rp-iris)")
        }

        if (isPickerOpen) {
            Div({
                style {
                    property("position", "absolute")
                    property("top", "105%")
                    property("left", "0")
                    property("width", "280px")
                    property("z-index", "250")
                    property("background", "var(--rp-info-bg)")
                    property("border-radius", "28px")
                    property("padding", "1.25rem 1rem")
                    property("box-shadow", "0 14px 35px rgba(0, 0, 0, 0.25)")
                    property("border", "1px solid var(--rp-hl-med)")
                }
            }) {
                Span({
                    style {
                        property("font-size", "0.78rem")
                        property("font-weight", "600")
                        property("color", "var(--rp-subtle)")
                        property("margin-bottom", "0.85rem")
                        property("display", "block")
                        property("padding-left", "0.25rem")
                    }
                }) {
                    Text("Select time")
                }

                Div({
                    style {
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("gap", "0.6rem")
                        property("margin-bottom", "1.25rem")
                    }
                }) {
                    Button({
                        onClick { activeTab = "HOUR" }
                        style {
                            property("width", "82px")
                            property("height", "64px")
                            property("border-radius", "12px")
                            property("border", "none")
                            property("background", if (activeTab == "HOUR") "var(--rp-info-border)" else "var(--rp-hl-med)")
                            property("color", if (activeTab == "HOUR") "var(--rp-iris)" else "var(--rp-text)")
                            property("font-size", "2.2rem")
                            property("font-weight", "700")
                            property("cursor", "pointer")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                        }
                    }) {
                        Text(currentH.toString().padStart(2, '0'))
                    }

                    Span({ style { property("font-size", "2rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                        Text(":")
                    }

                    Button({
                        onClick { activeTab = "MINUTE" }
                        style {
                            property("width", "82px")
                            property("height", "64px")
                            property("border-radius", "12px")
                            property("border", "none")
                            property("background", if (activeTab == "MINUTE") "var(--rp-info-border)" else "var(--rp-hl-med)")
                            property("color", if (activeTab == "MINUTE") "var(--rp-iris)" else "var(--rp-text)")
                            property("font-size", "2.2rem")
                            property("font-weight", "700")
                            property("cursor", "pointer")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                        }
                    }) {
                        Text(currentM.toString().padStart(2, '0'))
                    }
                }

                if (isTextInputMode) {
                    Div({
                        style {
                            property("display", "flex")
                            property("flex-direction", "column")
                            property("gap", "0.6rem")
                            property("margin", "1rem 0")
                        }
                    }) {
                        Input(InputType.Text) {
                            value(timeValue)
                            onInput { e -> onTimeChange(e.value) }
                            placeholder("HH:MM (t.ex 14:30)")
                            style {
                                property("padding", "0.6rem 0.85rem")
                                property("border-radius", "10px")
                                property("border", "1px solid var(--rp-iris)")
                                property("font-size", "1.1rem")
                                property("font-weight", "700")
                                property("text-align", "center")
                            }
                        }
                    }
                } else {
                    Div({
                        style {
                            property("position", "relative")
                            property("width", "220px")
                            property("height", "220px")
                            property("border-radius", "50%")
                            property("background", "var(--rp-hl-med)")
                            property("margin", "0 auto 1rem auto")
                            property("user-select", "none")
                        }
                    }) {
                        val centerX = 110.0
                        val centerY = 110.0

                        val isHourTab = activeTab == "HOUR"
                        val (armAngleDeg, armLength) = if (isHourTab) {
                            val isOuter = currentH == 0 || (currentH in 1..12)
                            val hPos = if (currentH == 0) 12 else if (currentH > 12) currentH - 12 else currentH
                            Pair(hPos * 30.0 - 90.0, if (isOuter) 82.0 else 52.0)
                        } else {
                            Pair(currentM * 6.0 - 90.0, 78.0)
                        }

                        // Center Pivot Dot
                        Div({
                            style {
                                property("position", "absolute")
                                property("left", "106px")
                                property("top", "106px")
                                property("width", "8px")
                                property("height", "8px")
                                property("border-radius", "50%")
                                property("background", "var(--rp-iris)")
                                property("z-index", "6")
                            }
                        }) {}

                        // Arm Line
                        Div({
                            style {
                                property("position", "absolute")
                                property("left", "110px")
                                property("top", "109px")
                                property("width", "${armLength}px")
                                property("height", "2.5px")
                                property("background", "var(--rp-iris)")
                                property("transform-origin", "0% 50%")
                                property("transform", "rotate(${armAngleDeg}deg)")
                                property("pointer-events", "none")
                                property("z-index", "5")
                            }
                        }) {}

                        if (isHourTab) {
                            val outerHours = listOf(12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11)
                            outerHours.forEach { hNum ->
                                val displayNum = if (hNum == 12) 0 else hNum
                                val angleRad = (hNum * 30.0 - 90.0) * (kotlin.math.PI / 180.0)
                                val x = centerX + 82.0 * kotlin.math.cos(angleRad)
                                val y = centerY + 82.0 * kotlin.math.sin(angleRad)
                                val isSelected = currentH == displayNum

                                Button({
                                    onClick {
                                        val newT = "${displayNum.toString().padStart(2, '0')}:${currentM.toString().padStart(2, '0')}"
                                        onTimeChange(newT)
                                        activeTab = "MINUTE"
                                    }
                                    style {
                                        property("position", "absolute")
                                        property("left", "${x - 16}px")
                                        property("top", "${y - 16}px")
                                        property("width", "32px")
                                        property("height", "32px")
                                        property("border-radius", "50%")
                                        property("border", "none")
                                        property("background", if (isSelected) "var(--rp-iris)" else "transparent")
                                        property("color", if (isSelected) "#FFFFFF" else "var(--rp-text)")
                                        property("font-size", "0.85rem")
                                        property("font-weight", if (isSelected) "800" else "600")
                                        property("cursor", "pointer")
                                        property("display", "flex")
                                        property("align-items", "center")
                                        property("justify-content", "center")
                                        property("z-index", "10")
                                    }
                                }) {
                                    Text(displayNum.toString().padStart(2, '0'))
                                }
                            }

                            val innerHours = listOf(24, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23)
                            innerHours.forEach { hNum ->
                                val displayNum = if (hNum == 24) 12 else hNum
                                val angleRad = ((hNum - 12) * 30.0 - 90.0) * (kotlin.math.PI / 180.0)
                                val x = centerX + 52.0 * kotlin.math.cos(angleRad)
                                val y = centerY + 52.0 * kotlin.math.sin(angleRad)
                                val isSelected = currentH == displayNum

                                Button({
                                    onClick {
                                        val newT = "${displayNum.toString().padStart(2, '0')}:${currentM.toString().padStart(2, '0')}"
                                        onTimeChange(newT)
                                        activeTab = "MINUTE"
                                    }
                                    style {
                                        property("position", "absolute")
                                        property("left", "${x - 14}px")
                                        property("top", "${y - 14}px")
                                        property("width", "28px")
                                        property("height", "28px")
                                        property("border-radius", "50%")
                                        property("border", "none")
                                        property("background", if (isSelected) "var(--rp-iris)" else "transparent")
                                        property("color", if (isSelected) "#FFFFFF" else "var(--rp-subtle)")
                                        property("font-size", "0.78rem")
                                        property("font-weight", if (isSelected) "800" else "600")
                                        property("cursor", "pointer")
                                        property("display", "flex")
                                        property("align-items", "center")
                                        property("justify-content", "center")
                                        property("z-index", "10")
                                    }
                                }) {
                                    Text(displayNum.toString())
                                }
                            }
                        } else {
                            val minutesList = listOf(0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55)
                            minutesList.forEach { mVal ->
                                val angleRad = (mVal * 6.0 - 90.0) * (kotlin.math.PI / 180.0)
                                val x = centerX + 78.0 * kotlin.math.cos(angleRad)
                                val y = centerY + 78.0 * kotlin.math.sin(angleRad)
                                val isSelected = currentM == mVal

                                Button({
                                    onClick {
                                        val newT = "${currentH.toString().padStart(2, '0')}:${mVal.toString().padStart(2, '0')}"
                                        onTimeChange(newT)
                                        isPickerOpen = false
                                    }
                                    style {
                                        property("position", "absolute")
                                        property("left", "${x - 16}px")
                                        property("top", "${y - 16}px")
                                        property("width", "32px")
                                        property("height", "32px")
                                        property("border-radius", "50%")
                                        property("border", "none")
                                        property("background", if (isSelected) "var(--rp-iris)" else "transparent")
                                        property("color", if (isSelected) "#FFFFFF" else "var(--rp-text)")
                                        property("font-size", "0.85rem")
                                        property("font-weight", if (isSelected) "800" else "600")
                                        property("cursor", "pointer")
                                        property("display", "flex")
                                        property("align-items", "center")
                                        property("justify-content", "center")
                                        property("z-index", "10")
                                    }
                                }) {
                                    Text(mVal.toString().padStart(2, '0'))
                                }
                            }
                        }
                    }
                }

                Div({
                    style {
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "space-between")
                        property("margin-top", "0.5rem")
                    }
                }) {
                    Button({
                        onClick { isTextInputMode = !isTextInputMode }
                        style {
                            property("background", "transparent")
                            property("border", "none")
                            property("color", "var(--rp-subtle)")
                            property("cursor", "pointer")
                            property("padding", "0.4rem")
                        }
                    }) {
                        FaIcon(if (isTextInputMode) "fa-regular fa-clock" else "fa-solid fa-keyboard", size = "1.1rem", color = "var(--rp-subtle)")
                    }

                    Div({
                        style {
                            property("display", "flex")
                            property("gap", "0.5rem")
                        }
                    }) {
                        Button({
                            onClick { isPickerOpen = false }
                            style {
                                property("background", "transparent")
                                property("border", "none")
                                property("color", "var(--rp-iris)")
                                property("font-weight", "700")
                                property("font-size", "0.85rem")
                                property("cursor", "pointer")
                                property("padding", "0.4rem 0.6rem")
                            }
                        }) {
                            Text("Cancel")
                        }
                        Button({
                            onClick { isPickerOpen = false }
                            style {
                                property("background", "transparent")
                                property("border", "none")
                                property("color", "var(--rp-iris)")
                                property("font-weight", "800")
                                property("font-size", "0.85rem")
                                property("cursor", "pointer")
                                property("padding", "0.4rem 0.6rem")
                            }
                        }) {
                            Text("OK")
                        }
                    }
                }
            }
        }
    }
}

// ──────── OverflowModal ────────

@Composable
internal fun OverflowModal(
    slots: List<SlotDto>,
    isManager: Boolean,
    currentUserId: Long?,
    onClaim: (Long) -> Unit,
    onUnclaim: (Long) -> Unit,
    onEdit: ((SlotDto) -> Unit)?,
    onAssign: ((Long, Long?) -> Unit)? = null,
    onClose: () -> Unit,
    title: String = "Alla pass",
    extraAction: (() -> Unit)? = null,
    extraActionLabel: String = "",
    staff: List<se.mittpass.shared.api.StaffMemberDto> = emptyList()
) {
    val groupedByDay = slots
        .sortedWith(compareBy({ it.day }, { it.startTime }))
        .groupBy { it.day }

    val collapsedDays = remember { mutableStateOf(setOf<String>()) }
    var assigningSlotKey by remember { mutableStateOf<String?>(null) }
    var staffSearchQuery by remember { mutableStateOf("") }

    Div({
        onClick { onClose() }
        style {
            property("position", "fixed"); property("inset", "0")
            property("background", "rgba(13,13,24,0.4)"); property("z-index", "100")
            property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
            property("backdrop-filter", "blur(3px)"); property("padding", "1rem")
        }
    }) {
        Div({
            onClick { it.stopPropagation() }
            style {
                property("background", "var(--rp-surface)"); property("border-radius", "20px")
                property("padding", "1.75rem 1.85rem"); property("width", "540px")
                property("max-width", "100%"); property("max-height", "82vh"); property("overflow-y", "auto")
                property("box-shadow", "0 24px 60px rgba(13,13,24,0.25)")
            }
        }) {
            H3({ style {
                property("margin", "0 0 0.25rem"); property("font-size", "1.15rem")
                property("font-weight", "700"); property("color", "var(--rp-text)")
            } }) { Text(title) }

            Div({
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "space-between")
                    property("margin", "0 0 1.25rem")
                }
            }) {
                P({ style {
                    property("margin", "0"); property("color", "var(--rp-muted)"); property("font-size", "0.875rem")
                } }) { Text("${slots.size} pass totalt fördelade på ${groupedByDay.size} dagar") }

                if (groupedByDay.size > 1) {
                    val allCollapsed = collapsedDays.value.size >= groupedByDay.size
                    Button({
                        onClick {
                            collapsedDays.value = if (allCollapsed) emptySet() else groupedByDay.keys.toSet()
                        }
                        style {
                            property("background", "transparent")
                            property("color", "var(--rp-pine)")
                            property("border", "none")
                            property("cursor", "pointer")
                            property("font-size", "0.8rem")
                            property("font-weight", "700")
                            property("padding", "0")
                            property("font-family", "inherit")
                        }
                    }) {
                        Text(if (allCollapsed) "Fäll ut alla" else "Fäll ihop alla")
                    }
                }
            }

            groupedByDay.forEach { (dayStr, daySlots) ->
                val isCollapsed = collapsedDays.value.contains(dayStr)

                // Sticky Date Section Header (Clickable for Collapse/Expand)
                Div({
                    onClick {
                        collapsedDays.value = if (isCollapsed) {
                            collapsedDays.value - dayStr
                        } else {
                            collapsedDays.value + dayStr
                        }
                    }
                    style {
                        property("position", "sticky")
                        property("top", "-1.75rem")
                        property("z-index", "10")
                        property("background", "var(--rp-surface)")
                        property("padding", "0.6rem 0")
                        property("border-bottom", "2px solid var(--rp-hl-med)")
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "space-between")
                        property("margin-top", "1rem")
                        property("margin-bottom", if (isCollapsed) "0.35rem" else "0.65rem")
                        property("cursor", "pointer")
                        property("user-select", "none")
                    }
                }) {
                    Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.55rem") } }) {
                        FaIcon(if (isCollapsed) "fa-solid fa-chevron-right" else "fa-solid fa-chevron-down", size = "0.75rem", color = "var(--rp-pine)")
                        FaIcon("fa-solid fa-calendar-day", size = "0.9rem", color = "var(--rp-pine)")
                        Span({
                            style {
                                property("font-weight", "800")
                                property("font-size", "0.92rem")
                                property("color", "var(--rp-text)")
                            }
                        }) {
                            Text(formatNiceDate(dayStr))
                        }
                    }
                    Span({
                        style {
                            property("background", if (isCollapsed) "var(--rp-overlay)" else "var(--rp-info-bg)")
                            property("color", if (isCollapsed) "var(--rp-subtle)" else "var(--rp-pine)")
                            property("padding", "0.2rem 0.65rem")
                            property("border-radius", "50px")
                            property("font-size", "0.75rem")
                            property("font-weight", "700")
                        }
                    }) {
                        Text("${daySlots.size} pass${if (isCollapsed) " (dolt)" else ""}")
                    }
                }

                if (!isCollapsed) {
                    val slotsByTime = daySlots.groupBy { "${it.startTime.take(5)} \u2013 ${it.endTime.take(5)}" }

                    slotsByTime.forEach { (timeStr, timeSlots) ->
                        val totalNeeded = timeSlots.size
                        val assignedSlots = timeSlots.filter { it.assignedToUserId != null }
                        val unassignedSlots = timeSlots.filter { it.assignedToUserId == null }
                        val assignedUserIds = assignedSlots.mapNotNull { it.assignedToUserId }.toSet()

                        val isOwn = assignedUserIds.contains(currentUserId)
                        val isFullyAssigned = assignedSlots.size == totalNeeded
                        val isPartiallyAssigned = assignedSlots.isNotEmpty() && !isFullyAssigned

                        val allApplicants = timeSlots.flatMap { it.interestedUsers }.distinctBy { it.id }
                        val hasClaimed = allApplicants.any { it.id == currentUserId }
                        val isFilledByOther = timeSlots.all { it.isFilledByOther(currentUserId) }

                        val cardBg = when {
                            isOwn -> "var(--rp-ok-bg)"
                            isFullyAssigned -> "var(--rp-ok-bg)"
                            isPartiallyAssigned -> "var(--rp-warn-bg)"
                            isFilledByOther -> "var(--rp-overlay)"
                            hasClaimed -> "var(--rp-info-bg)"
                            else -> "var(--rp-info-bg)"
                        }
                        val borderLeft = when {
                            isOwn -> "4px solid var(--rp-ok-text)"
                            isFullyAssigned -> "4px solid var(--rp-ok-text)"
                            isPartiallyAssigned -> "4px solid var(--rp-warn-text)"
                            isFilledByOther -> "4px solid var(--rp-muted)"
                            hasClaimed -> "4px solid var(--rp-pine)"
                            else -> "4px solid var(--rp-pine)"
                        }

                        Div({
                            style {
                                property("background", cardBg)
                                property("border-left", borderLeft)
                                property("border-radius", "10px")
                                property("padding", "0.85rem 1rem")
                                property("margin-bottom", "0.65rem")
                                property("display", "flex")
                                property("flex-direction", "column")
                                property("gap", "0.55rem")
                                property("box-shadow", "0 1px 3px rgba(0,0,0,0.04)")
                            }
                        }) {
                            // Header Row: Time string, Capacity/Status badge, Edit button
                            Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("align-items", "center") } }) {
                                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.6rem") } }) {
                                    Span({ style { property("font-weight", "800"); property("font-size", "0.95rem"); property("color", "var(--rp-text)") } }) {
                                        Text(timeStr)
                                    }

                                    if (totalNeeded > 1) {
                                        Span({
                                            style {
                                                property("background", if (isFullyAssigned) "var(--rp-ok-bg)" else if (isPartiallyAssigned) "var(--rp-warn-bg)" else "var(--rp-info-bg)")
                                                property("color", if (isFullyAssigned) "var(--rp-ok-text)" else if (isPartiallyAssigned) "var(--rp-warn-text)" else "var(--rp-pine)")
                                                property("border-radius", "50px")
                                                property("padding", "0.2rem 0.65rem")
                                                property("font-size", "0.75rem")
                                                property("font-weight", "800")
                                            }
                                        }) {
                                            Text("${assignedSlots.size} av $totalNeeded tillsatta")
                                        }
                                    } else if (isFullyAssigned) {
                                        Span({
                                            style {
                                                property("background", "var(--rp-ok-bg)")
                                                property("color", "var(--rp-ok-text)")
                                                property("border", "1px solid var(--rp-ok-border)")
                                                property("border-radius", "50px")
                                                property("padding", "0.2rem 0.65rem")
                                                property("font-size", "0.75rem")
                                                property("font-weight", "800")
                                                property("display", "inline-flex")
                                                property("align-items", "center")
                                                property("gap", "0.3rem")
                                            }
                                        }) {
                                            FaIcon("fa-solid fa-circle-check", size = "0.72rem", color = "var(--rp-ok-text)")
                                            Text("Tilldelad")
                                        }
                                    } else if (isFilledByOther) {
                                        Span({
                                            style {
                                                property("background", "var(--rp-overlay)")
                                                property("color", "var(--rp-subtle)")
                                                property("border", "1px solid var(--rp-hl-med)")
                                                property("border-radius", "50px")
                                                property("padding", "0.2rem 0.65rem")
                                                property("font-size", "0.75rem")
                                                property("font-weight", "700")
                                            }
                                        }) {
                                            Text("Tillsatt av kollega")
                                        }
                                    } else if (hasClaimed) {
                                        Span({
                                            style {
                                                property("background", "var(--rp-warn-bg)")
                                                property("color", "var(--rp-warn-text)")
                                                property("border", "1px solid var(--rp-warn-border)")
                                                property("border-radius", "50px")
                                                property("padding", "0.2rem 0.65rem")
                                                property("font-size", "0.75rem")
                                                property("font-weight", "800")
                                            }
                                        }) {
                                            Text("Sökt (Väntar)")
                                        }
                                    } else {
                                        Span({
                                            style {
                                                property("background", if (hasClaimed) "var(--rp-info-border)" else "var(--rp-info-bg)")
                                                property("color", if (hasClaimed) "var(--rp-pine)" else "var(--rp-pine)")
                                                property("border-radius", "50px")
                                                property("padding", "0.2rem 0.65rem")
                                                property("font-size", "0.75rem")
                                                property("font-weight", "700")
                                            }
                                        }) {
                                            Text(if (allApplicants.isNotEmpty()) "${allApplicants.size} ansökningar" else "Öppet pass")
                                        }
                                    }
                                }

                                Div({ style { property("display", "flex"); property("gap", "0.4rem"); property("align-items", "center") } }) {
                                    if (isManager && unassignedSlots.isNotEmpty() && onAssign != null) {
                                        val slotKey = "$dayStr-$timeStr"
                                        val isAssigningThis = assigningSlotKey == slotKey
                                        Button({
                                            onClick {
                                                if (isAssigningThis) {
                                                    assigningSlotKey = null
                                                } else {
                                                    assigningSlotKey = slotKey
                                                    staffSearchQuery = ""
                                                }
                                            }
                                            style {
                                                property("background", if (isAssigningThis) "var(--rp-pine)" else "var(--rp-pine)"); property("color", "var(--rp-surface)")
                                                property("border", "none"); property("border-radius", "8px")
                                                property("padding", "0.35rem 0.75rem"); property("cursor", "pointer")
                                                property("font-weight", "700"); property("font-size", "0.78rem")
                                                property("font-family", "inherit")
                                                property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.35rem")
                                            }
                                        }) {
                                            FaIcon(if (isAssigningThis) "fa-solid fa-xmark" else "fa-solid fa-user-plus", size = "0.72rem", color = "var(--rp-surface)")
                                            Text(if (isAssigningThis) "Avbryt" else "Tilldela")
                                        }
                                    }

                                    if (isManager && onEdit != null) {
                                        val editSlot = unassignedSlots.firstOrNull() ?: timeSlots.first()
                                        Button({
                                            onClick { onEdit(editSlot) }
                                            style {
                                                property("background", "transparent"); property("color", "var(--rp-pine)")
                                                property("border", "1px solid var(--rp-info-border)"); property("border-radius", "8px")
                                                property("padding", "0.35rem 0.75rem"); property("cursor", "pointer")
                                                property("font-weight", "700"); property("font-size", "0.78rem")
                                                property("font-family", "inherit")
                                            }
                                        }) { Text("Redigera") }
                                    } else if (!isManager) {
                                        if (!isFullyAssigned && !hasClaimed && unassignedSlots.isNotEmpty()) {
                                            val openSlot = unassignedSlots.first()
                                            Button({
                                                onClick { 
                                                    onClose()
                                                    onClaim(openSlot.id) 
                                                }
                                                style {
                                                    property("background", "var(--rp-pine)"); property("color", "var(--rp-surface)")
                                                    property("border", "none"); property("border-radius", "8px")
                                                    property("padding", "0.4rem 0.85rem"); property("cursor", "pointer")
                                                    property("font-weight", "700"); property("font-size", "0.8rem")
                                                    property("font-family", "inherit")
                                                }
                                            }) { Text("Anmäl intresse") }
                                        } else if (!isFullyAssigned && hasClaimed) {
                                            val claimedSlot = timeSlots.firstOrNull { s -> s.interestedUsers.any { it.id == currentUserId } } ?: timeSlots.first()
                                            Button({
                                                onClick { 
                                                    onClose()
                                                    onUnclaim(claimedSlot.id) 
                                                }
                                                style {
                                                    property("background", "transparent"); property("color", "var(--rp-love)")
                                                    property("border", "1px solid var(--rp-err-border)"); property("border-radius", "8px")
                                                    property("padding", "0.4rem 0.85rem"); property("cursor", "pointer")
                                                    property("font-weight", "700"); property("font-size", "0.8rem")
                                                    property("font-family", "inherit")
                                                }
                                            }) { Text("Ångra intresse") }
                                        } else if (isOwn) {
                                            val ownSlot = assignedSlots.firstOrNull { it.assignedToUserId == currentUserId } ?: timeSlots.first()
                                            Button({
                                                onClick { 
                                                    onClose()
                                                    onUnclaim(ownSlot.id) 
                                                }
                                                style {
                                                    property("background", "transparent"); property("color", "var(--rp-love)")
                                                    property("border", "1px solid var(--rp-err-border)"); property("border-radius", "8px")
                                                    property("padding", "0.4rem 0.85rem"); property("cursor", "pointer")
                                                    property("font-weight", "700"); property("font-size", "0.8rem")
                                                    property("font-family", "inherit")
                                                }
                                            }) { Text("Lämna passet") }
                                        }
                                    }
                                }
                            }

                            // 1. Render Assigned Personnel Rows
                            if (assignedSlots.isNotEmpty()) {
                                Div({
                                    style {
                                        property("display", "flex"); property("flex-direction", "column")
                                        property("gap", "0.4rem"); property("padding-top", "0.35rem")
                                        property("border-top", "1px solid rgba(0,0,0,0.06)")
                                    }
                                }) {
                                    assignedSlots.forEach { slot ->
                                        val staffMember = staff.find { it.userId == slot.assignedToUserId }
                                        val assignedName = staffMember?.let { member ->
                                            listOfNotNull(member.firstName, member.lastName).joinToString(" ").ifEmpty { null }
                                        } ?: slot.assignedToEmail ?: "Personal-ID: ${slot.assignedToUserId}"
                                        val memberScheme = getEmployeeColorScheme(staffMember?.color, slot.assignedToUserId)

                                        Div({
                                            style {
                                                property("display", "flex"); property("justify-content", "space-between")
                                                property("align-items", "center"); property("background", "var(--rp-surface)")
                                                property("padding", "0.45rem 0.75rem"); property("border-radius", "8px")
                                                property("border", "1px solid var(--rp-ok-bg)")
                                                property("border-left", "3px solid ${memberScheme.primary}")
                                            }
                                        }) {
                                            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.45rem") } }) {
                                                Div({
                                                    style {
                                                        property("width", "10px"); property("height", "10px")
                                                        property("border-radius", "50%")
                                                        property("background", memberScheme.primary)
                                                        property("flex-shrink", "0")
                                                    }
                                                })
                                                Span({ style { property("font-size", "0.85rem"); property("font-weight", "700"); property("color", "var(--rp-ok-text)") } }) {
                                                    Text(assignedName)
                                                }
                                                Span({
                                                    style {
                                                        property("background", "var(--rp-ok-bg)"); property("color", "var(--rp-pine)")
                                                        property("border-radius", "50px"); property("padding", "0.15rem 0.5rem")
                                                        property("font-size", "0.72rem"); property("font-weight", "800")
                                                    }
                                                }) { Text("Tilldelad") }
                                            }

                                            if (isManager && onAssign != null) {
                                                Button({
                                                    onClick { onAssign(slot.id, null) }
                                                    style {
                                                        property("background", "var(--rp-err-bg)"); property("color", "var(--rp-love)")
                                                        property("border", "1px solid var(--rp-err-border)"); property("border-radius", "6px")
                                                        property("padding", "0.3rem 0.65rem"); property("cursor", "pointer")
                                                        property("font-weight", "700"); property("font-size", "0.75rem")
                                                        property("font-family", "inherit")
                                                    }
                                                }) { Text("Avtilldela") }
                                            }
                                        }
                                    }
                                }
                            }

                            // 2. Render Assignment Panel (Applicants & Full Employee Directory with Search)
                            if (isManager && unassignedSlots.isNotEmpty() && onAssign != null) {
                                val slotKey = "$dayStr-$timeStr"
                                val isAssigningThis = assigningSlotKey == slotKey
                                val remainingApplicants = allApplicants.filter { user -> user.id !in assignedUserIds }
                                val nextOpenSlot = unassignedSlots.first()
                                val nextStartMin = parseMinutes(nextOpenSlot.startTime)
                                val nextEndMin = parseMinutes(nextOpenSlot.endTime)

                                // If user clicked "Tilldela" on this shift, or if applicants exist and nobody clicked another shift
                                val showAssignPanel = isAssigningThis || (remainingApplicants.isNotEmpty() && assigningSlotKey == null)

                                if (showAssignPanel) {
                                    Div({
                                        style {
                                            property("margin-top", "0.4rem")
                                            property("border-top", "1px solid var(--rp-hl-med)")
                                            property("padding-top", "0.55rem")
                                            property("display", "flex")
                                            property("flex-direction", "column")
                                            property("gap", "0.5rem")
                                        }
                                    }) {
                                        // A. Quick-Pick Applicants Section
                                        if (remainingApplicants.isNotEmpty()) {
                                            Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "0.35rem") } }) {
                                                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.35rem") } }) {
                                                    FaIcon("fa-solid fa-hand", size = "0.72rem", color = "var(--rp-warn-text)")
                                                    Span({
                                                        style {
                                                            property("font-size", "0.75rem")
                                                            property("font-weight", "800")
                                                            property("color", "var(--rp-warn-text)")
                                                            property("text-transform", "uppercase")
                                                            property("letter-spacing", "0.03em")
                                                        }
                                                    }) { Text("Sökta / intresserade (${remainingApplicants.size}):") }
                                                }

                                                remainingApplicants.forEach { user ->
                                                    val staffMember = staff.find { it.userId == user.id }
                                                    val displayName = staffMember?.let { member ->
                                                        listOfNotNull(member.firstName, member.lastName).joinToString(" ").ifEmpty { null }
                                                    } ?: user.email
                                                    val savedAvatar = kotlinx.browser.localStorage.getItem("mittpass_avatar_${user.id}")
                                                    val initial = displayName.take(1).uppercase()

                                                    val isAlreadyAssignedOnTime = slots.any { s ->
                                                        s.id != nextOpenSlot.id &&
                                                        s.assignedToUserId == user.id &&
                                                        s.day == dayStr &&
                                                        maxOf(nextStartMin, parseMinutes(s.startTime)) < minOf(nextEndMin, parseMinutes(s.endTime))
                                                    }

                                                    Div({
                                                        style {
                                                            property("display", "flex")
                                                            property("justify-content", "space-between")
                                                            property("align-items", "center")
                                                            property("background", "var(--rp-surface)")
                                                            property("padding", "0.4rem 0.65rem")
                                                            property("border-radius", "8px")
                                                            property("border", "1px solid var(--rp-warn-border)")
                                                            property("border-left", "3px solid var(--rp-warn-border)")
                                                        }
                                                    }) {
                                                        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                                                            Div({
                                                                style {
                                                                    property("width", "26px"); property("height", "26px")
                                                                    property("border-radius", "50%")
                                                                    if (!savedAvatar.isNullOrBlank()) property("background-image", "url('$savedAvatar')")
                                                                    if (!savedAvatar.isNullOrBlank()) property("background-size", "cover")
                                                                    if (!savedAvatar.isNullOrBlank()) property("background-position", "center")
                                                                    if (savedAvatar.isNullOrBlank()) property("background", "var(--rp-warn-bg)")
                                                                    property("color", "var(--rp-warn-text)"); property("font-weight", "800")
                                                                    property("font-size", "0.75rem"); property("display", "flex")
                                                                    property("align-items", "center"); property("justify-content", "center")
                                                                    property("overflow", "hidden"); property("flex-shrink", "0")
                                                                }
                                                            }) {
                                                                if (savedAvatar.isNullOrBlank()) Text(initial)
                                                            }
                                                            Span({
                                                                style {
                                                                    property("font-size", "0.825rem"); property("font-weight", "700")
                                                                    property("color", "var(--rp-text)")
                                                                }
                                                            }) { Text(displayName) }
                                                        }

                                                        if (isAlreadyAssignedOnTime) {
                                                            Span({
                                                                style {
                                                                    property("background", "var(--rp-overlay)"); property("color", "var(--rp-subtle)")
                                                                    property("border", "1px solid var(--rp-hl-med)"); property("border-radius", "6px")
                                                                    property("padding", "0.25rem 0.55rem"); property("font-size", "0.72rem")
                                                                    property("font-weight", "700")
                                                                }
                                                                attr("title", "Personen har redan ett överlappande pass denna tid")
                                                            }) { Text("Upptagen") }
                                                        } else {
                                                            Button({
                                                                onClick {
                                                                    onAssign(nextOpenSlot.id, user.id)
                                                                    assigningSlotKey = null
                                                                }
                                                                style {
                                                                    property("background", "var(--rp-pine)"); property("color", "var(--rp-surface)")
                                                                    property("border", "none"); property("border-radius", "6px")
                                                                    property("padding", "0.3rem 0.75rem"); property("cursor", "pointer")
                                                                    property("font-weight", "700"); property("font-size", "0.78rem")
                                                                    property("font-family", "inherit")
                                                                }
                                                            }) { Text("Välj") }
                                                        }
                                                    }
                                                }
                                            }
                                        }

                                        // B. All Employees Directory Search & Pick (Shown when manager clicks "Tilldela" or expands)
                                        if (isAssigningThis) {
                                            Div({
                                                style {
                                                    property("display", "flex")
                                                    property("flex-direction", "column")
                                                    property("gap", "0.35rem")
                                                    property("background", "var(--rp-overlay)")
                                                    property("border", "1px solid var(--rp-hl-med)")
                                                    property("border-radius", "10px")
                                                    property("padding", "0.6rem")
                                                }
                                            }) {
                                                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.35rem") } }) {
                                                    FaIcon("fa-solid fa-users", size = "0.72rem", color = "var(--rp-pine)")
                                                    Span({
                                                        style {
                                                            property("font-size", "0.75rem")
                                                            property("font-weight", "800")
                                                            property("color", "var(--rp-pine)")
                                                            property("text-transform", "uppercase")
                                                            property("letter-spacing", "0.03em")
                                                        }
                                                    }) { Text("Välj bland alla medarbetare:") }
                                                }

                                                // Search Input
                                                Div({
                                                    style {
                                                        property("display", "flex")
                                                        property("align-items", "center")
                                                        property("gap", "0.45rem")
                                                        property("background", "var(--rp-surface)")
                                                        property("border", "1px solid var(--rp-hl-med)")
                                                        property("border-radius", "8px")
                                                        property("padding", "0.35rem 0.6rem")
                                                    }
                                                }) {
                                                    FaIcon("fa-solid fa-magnifying-glass", size = "0.72rem", color = "var(--rp-muted)")
                                                    Input(InputType.Text) {
                                                        value(staffSearchQuery)
                                                        onInput { staffSearchQuery = it.value }
                                                        placeholder("Sök medarbetare...")
                                                        style {
                                                            property("border", "none")
                                                            property("outline", "none")
                                                            property("width", "100%")
                                                            property("font-size", "0.8rem")
                                                            property("font-family", "inherit")
                                                            property("background", "transparent")
                                                        }
                                                    }
                                                    if (staffSearchQuery.isNotBlank()) {
                                                        Button({
                                                            onClick { staffSearchQuery = "" }
                                                            style {
                                                                property("background", "transparent")
                                                                property("border", "none")
                                                                property("cursor", "pointer")
                                                                property("padding", "0")
                                                            }
                                                        }) {
                                                            FaIcon("fa-solid fa-xmark", size = "0.72rem", color = "var(--rp-muted)")
                                                        }
                                                    }
                                                }

                                                // Filtered Staff List
                                                val q = staffSearchQuery.trim().lowercase()
                                                val filteredStaff = staff
                                                    .filter { member ->
                                                        member.userId !in assignedUserIds &&
                                                        (q.isEmpty() ||
                                                         (member.firstName ?: "").lowercase().contains(q) ||
                                                         (member.lastName ?: "").lowercase().contains(q) ||
                                                         member.role.lowercase().contains(q))
                                                    }

                                                if (filteredStaff.isEmpty()) {
                                                    Div({
                                                        style {
                                                            property("text-align", "center")
                                                            property("padding", "0.5rem")
                                                            property("color", "var(--rp-subtle)")
                                                            property("font-size", "0.75rem")
                                                        }
                                                    }) {
                                                        Text(if (staff.isEmpty()) "Ingen personal registrerad." else "Ingen medarbetare matchar sökningen.")
                                                    }
                                                } else {
                                                    Div({
                                                        style {
                                                            property("display", "flex")
                                                            property("flex-direction", "column")
                                                            property("gap", "0.3rem")
                                                            property("max-height", "170px")
                                                            property("overflow-y", "auto")
                                                            property("padding-right", "0.15rem")
                                                        }
                                                    }) {
                                                        filteredStaff.forEach { member ->
                                                            val uId = member.userId
                                                            val fullName = listOfNotNull(member.firstName, member.lastName).joinToString(" ").ifEmpty { "Anställd #$uId" }
                                                            val memberScheme = getEmployeeColorScheme(member.color, uId)
                                                            val savedAvatar = kotlinx.browser.localStorage.getItem("mittpass_avatar_$uId")
                                                            val initial = fullName.take(1).uppercase()

                                                            val isAlreadyAssignedOnTime = slots.any { s ->
                                                                s.id != nextOpenSlot.id &&
                                                                s.assignedToUserId == uId &&
                                                                s.day == dayStr &&
                                                                maxOf(nextStartMin, parseMinutes(s.startTime)) < minOf(nextEndMin, parseMinutes(s.endTime))
                                                            }

                                                            Div({
                                                                style {
                                                                    property("display", "flex")
                                                                    property("justify-content", "space-between")
                                                                    property("align-items", "center")
                                                                    property("background", "var(--rp-surface)")
                                                                    property("padding", "0.35rem 0.6rem")
                                                                    property("border-radius", "6px")
                                                                    property("border", "1px solid var(--rp-hl-med)")
                                                                    property("border-left", "3px solid ${memberScheme.primary}")
                                                                }
                                                            }) {
                                                                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.45rem") } }) {
                                                                    Div({
                                                                        style {
                                                                            property("width", "24px"); property("height", "24px")
                                                                            property("border-radius", "50%")
                                                                            if (!savedAvatar.isNullOrBlank()) property("background-image", "url('$savedAvatar')")
                                                                            if (!savedAvatar.isNullOrBlank()) property("background-size", "cover")
                                                                            if (!savedAvatar.isNullOrBlank()) property("background-position", "center")
                                                                            if (savedAvatar.isNullOrBlank()) property("background", memberScheme.bg)
                                                                            property("color", memberScheme.text); property("font-weight", "800")
                                                                            property("font-size", "0.72rem"); property("display", "flex")
                                                                            property("align-items", "center"); property("justify-content", "center")
                                                                            property("overflow", "hidden"); property("flex-shrink", "0")
                                                                        }
                                                                    }) {
                                                                        if (savedAvatar.isNullOrBlank()) Text(initial)
                                                                    }
                                                                    Div {
                                                                        Span({
                                                                            style {
                                                                                property("font-size", "0.8rem"); property("font-weight", "700")
                                                                                property("color", "var(--rp-text)"); property("display", "block")
                                                                            }
                                                                        }) { Text(fullName) }
                                                                        if (member.role.isNotBlank()) {
                                                                            Span({
                                                                                style {
                                                                                    property("font-size", "0.68rem"); property("color", "var(--rp-subtle)")
                                                                                    property("display", "block"); property("line-height", "1")
                                                                                }
                                                                            }) { Text(member.role) }
                                                                        }
                                                                    }
                                                                }

                                                                if (isAlreadyAssignedOnTime) {
                                                                    Span({
                                                                        style {
                                                                            property("background", "var(--rp-overlay)"); property("color", "var(--rp-subtle)")
                                                                            property("border", "1px solid var(--rp-hl-med)"); property("border-radius", "6px")
                                                                            property("padding", "0.22rem 0.5rem"); property("font-size", "0.7rem")
                                                                            property("font-weight", "700")
                                                                        }
                                                                        attr("title", "Personen har redan ett överlappande pass denna tid")
                                                                    }) { Text("Upptagen") }
                                                                } else {
                                                                    Button({
                                                                        onClick {
                                                                            onAssign(nextOpenSlot.id, uId)
                                                                            assigningSlotKey = null
                                                                        }
                                                                        style {
                                                                            property("background", "var(--rp-pine)"); property("color", "var(--rp-surface)")
                                                                            property("border", "none"); property("border-radius", "6px")
                                                                            property("padding", "0.28rem 0.65rem"); property("cursor", "pointer")
                                                                            property("font-weight", "700"); property("font-size", "0.75rem")
                                                                            property("font-family", "inherit")
                                                                        }
                                                                    }) { Text("Välj") }
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            Div({ style {
                property("display", "flex"); property("gap", "0.55rem"); property("justify-content", "flex-end"); property("margin-top", "1rem")
            } }) {
                if (extraAction != null) {
                    Button({
                        onClick { extraAction() }
                        style {
                            property("background", "var(--rp-pine)"); property("color", "var(--rp-surface)")
                            property("border", "none"); property("border-radius", "10px")
                            property("padding", "0.65rem 1.2rem"); property("cursor", "pointer")
                            property("font-weight", "700"); property("font-size", "0.9rem")
                            property("font-family", "inherit"); property("margin-right", "auto")
                        }
                    }) { Text(extraActionLabel) }
                }
                Button({
                    onClick { onClose() }
                    style {
                        property("background", "transparent"); property("color", "var(--rp-subtle)")
                        property("border", "1px solid var(--rp-hl-med)"); property("border-radius", "10px")
                        property("padding", "0.65rem 1.2rem"); property("cursor", "pointer")
                        property("font-weight", "600"); property("font-size", "0.9rem")
                        property("font-family", "inherit")
                    }
                }) { Text("Stäng") }
            }
        }
    }
}

// ──────── ConfirmClaimDialog ────────

@Composable
internal fun ConfirmClaimDialog(
    isUnclaim: Boolean,
    day: String,
    startTime: String,
    endTime: String,
    onConfirm: () -> Unit,
    onCancel: () -> Unit
) {
    val title = if (isUnclaim) "Ta bort intresseanmälan?" else "Anmäl intresse?"
    val body = if (isUnclaim)
        "Vill du ta bort din intresseanmälan för passet ${formatNiceDate(day)} ${startTime.take(5)}–${endTime.take(5)}?"
    else
        "Vill du anmäla intresse för passet ${formatNiceDate(day)} ${startTime.take(5)}–${endTime.take(5)}?"
    val confirmLabel = if (isUnclaim) "Ta bort intresse" else "Ta passet"
    val confirmColor = if (isUnclaim) "var(--rp-err-text)" else "var(--rp-pine)"

    Div({
        onClick { onCancel() }
        style {
            property("position", "fixed"); property("inset", "0")
            property("background", "rgba(13,13,24,0.4)"); property("z-index", "200")
            property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
            property("backdrop-filter", "blur(3px)"); property("padding", "1rem")
        }
    }) {
        Div({
            onClick { it.stopPropagation() }
            style {
                property("background", "var(--rp-surface)"); property("border-radius", "20px")
                property("padding", "1.75rem 1.85rem"); property("width", "400px")
                property("max-width", "100%"); property("box-shadow", "0 24px 60px rgba(13,13,24,0.25)")
            }
        }) {
            H3({ style {
                property("margin", "0 0 0.5rem"); property("font-size", "1.15rem")
                property("font-weight", "700"); property("color", "var(--rp-text)")
            } }) { Text(title) }
            P({ style {
                property("margin", "0 0 1.5rem"); property("color", "var(--rp-subtle)"); property("font-size", "0.9rem")
                property("line-height", "1.5")
            } }) { Text(body) }
            Div({ style {
                property("display", "flex"); property("gap", "0.55rem"); property("justify-content", "flex-end")
            } }) {
                Button({
                    onClick { onCancel() }
                    style {
                        property("background", "transparent"); property("color", "var(--rp-subtle)")
                        property("border", "1px solid var(--rp-hl-med)"); property("border-radius", "10px")
                        property("padding", "0.65rem 1.1rem"); property("cursor", "pointer")
                        property("font-weight", "600"); property("font-size", "0.9rem")
                        property("font-family", "inherit")
                    }
                }) { Text("Avbryt") }
                Button({
                    onClick { onConfirm() }
                    style {
                        property("background", confirmColor); property("color", "var(--rp-surface)")
                        property("border", "none"); property("border-radius", "10px")
                        property("padding", "0.65rem 1.3rem"); property("cursor", "pointer")
                        property("font-weight", "700"); property("font-size", "0.9rem")
                        property("font-family", "inherit")
                    }
                }) { Text(confirmLabel) }
            }
        }
    }
}

// ──────── DayWindowSettings ────────

@Composable
internal fun DayWindowSettings(
    workplace: WorkplaceDto,
    onSave: (start: Int, end: Int) -> Unit,
    onCancel: () -> Unit
) {
    var start by remember { mutableStateOf(workplace.dayStartHour.coerceIn(0, 23)) }
    var end by remember { mutableStateOf(workplace.dayEndHour.coerceIn(1, 24)) }
    var saving by remember { mutableStateOf(false) }

    Div({
        onClick { onCancel() }
        style {
            property("position", "fixed"); property("inset", "0")
            property("background", "rgba(13,13,24,0.4)"); property("z-index", "100")
            property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
            property("backdrop-filter", "blur(3px)"); property("padding", "1rem")
        }
    }) {
        Div({
            onClick { it.stopPropagation() }
            style {
                property("background", "var(--rp-surface)"); property("border-radius", "20px")
                property("padding", "1.75rem 1.85rem"); property("width", "420px"); property("max-width", "100%")
                property("box-shadow", "0 24px 60px rgba(13,13,24,0.25)")
            }
        }) {
            H3({ style {
                property("margin", "0 0 0.25rem"); property("font-size", "1.15rem")
                property("font-weight", "700"); property("color", "var(--rp-text)")
            } }) { Text("Visningstider") }
            P({ style {
                property("margin", "0 0 1.4rem"); property("color", "var(--rp-muted)"); property("font-size", "0.875rem")
            } }) { Text("Vilka timmar visas i veckoschemat? Schemat anpassar sig automatiskt om pass läggs utanför.") }

            Div({ style {
                property("display", "grid"); property("grid-template-columns", "1fr 1fr"); property("gap", "0.85rem"); property("margin-bottom", "1.5rem")
            } }) {
                Div {
                    Span({ style { schemaModalLabel() } }) { Text("Från") }
                    Select({
                        attr("value", start.toString())
                        onChange { start = (it.target.asDynamic().value as String).toInt() }
                        style { schemaModalInput() }
                    }) {
                        for (h in 0..23) Option(h.toString()) { Text("${h.toString().padStart(2, '0')}:00") }
                    }
                }
                Div {
                    Span({ style { schemaModalLabel() } }) { Text("Till") }
                    Select({
                        attr("value", end.toString())
                        onChange { end = (it.target.asDynamic().value as String).toInt() }
                        style { schemaModalInput() }
                    }) {
                        for (h in 1..24) Option(h.toString()) { Text("${h.toString().padStart(2, '0')}:00") }
                    }
                }
            }

            if (start >= end) {
                P({ style {
                    property("color", "var(--rp-err-text)"); property("font-size", "0.85rem"); property("margin", "0 0 1rem")
                    property("font-weight", "600")
                } }) { Text("Starttiden måste vara före sluttiden.") }
            }

            Div({ style {
                property("display", "flex"); property("gap", "0.55rem"); property("justify-content", "flex-end")
            } }) {
                Button({
                    onClick { onCancel() }
                    style {
                        property("background", "transparent"); property("color", "var(--rp-subtle)")
                        property("border", "1px solid var(--rp-hl-med)"); property("border-radius", "10px")
                        property("padding", "0.65rem 1.1rem"); property("cursor", "pointer")
                        property("font-weight", "600"); property("font-size", "0.9rem")
                        property("font-family", "inherit")
                    }
                }) { Text("Avbryt") }
                Button({
                    onClick { saving = true; onSave(start, end) }
                    if (saving || start >= end) disabled()
                    style {
                        property("background", "var(--rp-pine)"); property("color", "var(--rp-surface)")
                        property("border", "none"); property("border-radius", "10px")
                        property("padding", "0.65rem 1.3rem"); property("cursor", "pointer")
                        property("font-weight", "700"); property("font-size", "0.9rem")
                        property("font-family", "inherit")
                        if (saving || start >= end) property("opacity", "0.6")
                    }
                }) { Text(if (saving) "Sparar…" else "Spara") }
            }
        }
    }
}

@Composable
internal fun AbsenceModal(
    absence: se.mittpass.shared.api.AbsenceRequestDto,
    staffMemberName: String?,
    isManager: Boolean,
    onApprove: (() -> Unit)? = null,
    onReject: (() -> Unit)? = null,
    onDelete: (() -> Unit)? = null,
    onClose: () -> Unit
) {
    var loading by remember { mutableStateOf(false) }

    val statusBg = when (absence.status.lowercase()) {
        "approved" -> "var(--rp-ok-bg)"; "pending" -> "var(--rp-warn-bg)"; else -> "var(--rp-err-bg)"
    }
    val statusColor = when (absence.status.lowercase()) {
        "approved" -> "var(--rp-ok-text)"; "pending" -> "var(--rp-warn-text)"; else -> "var(--rp-err-text)"
    }
    val statusLabel = when (absence.status.lowercase()) {
        "approved" -> "Godkänd"; "pending" -> "Väntar godkännande"; else -> "Nekad / Avslagen"
    }
    val typeLabel = when (absence.type.lowercase()) {
        "vacation" -> "Semester"; "sick" -> "Sjukanmälan"; "vab" -> "VAB"; else -> "Frånvaro"
    }

    Div({
        onClick { onClose() }
        style {
            property("position", "fixed"); property("inset", "0")
            property("background", "rgba(13,13,24,0.4)"); property("z-index", "250")
            property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
            property("backdrop-filter", "blur(3px)"); property("padding", "1rem")
        }
    }) {
        Div({
            onClick { it.stopPropagation() }
            style {
                property("background", "var(--rp-surface)"); property("border-radius", "20px")
                property("padding", "1.75rem 1.85rem"); property("width", "420px")
                property("max-width", "100%"); property("box-shadow", "0 24px 60px rgba(13,13,24,0.25)")
            }
        }) {
            Div({
                style {
                    property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between")
                    property("margin-bottom", "1rem")
                }
            }) {
                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.6rem") } }) {
                    Div({
                        style {
                            property("width", "38px"); property("height", "38px"); property("border-radius", "10px")
                            property("background", "var(--rp-info-bg)"); property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                        }
                    }) {
                        FaIcon("fa-solid fa-umbrella-beach", size = "1.1rem", color = "var(--rp-pine)")
                    }
                    Div {
                        H3({ style { property("margin", "0"); property("font-size", "1.15rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                            Text(typeLabel)
                        }
                        Span({ style { property("font-size", "0.8rem"); property("color", "var(--rp-subtle)"); property("font-weight", "600") } }) {
                            Text(staffMemberName ?: "Anställd")
                        }
                    }
                }
                Span({
                    style {
                        property("padding", "0.3rem 0.75rem"); property("border-radius", "50px")
                        property("font-size", "0.75rem"); property("font-weight", "800")
                        property("background", statusBg); property("color", statusColor)
                    }
                }) { Text(statusLabel) }
            }

            Div({
                style {
                    property("background", "var(--rp-overlay)"); property("border", "1px solid var(--rp-overlay)"); property("border-radius", "12px")
                    property("padding", "1rem"); property("margin-bottom", "1.25rem"); property("display", "flex")
                    property("flex-direction", "column"); property("gap", "0.6rem")
                }
            }) {
                Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("font-size", "0.85rem") } }) {
                    Span({ style { property("color", "var(--rp-subtle)"); property("font-weight", "600") } }) { Text("Datum:") }
                    Span({ style { property("color", "var(--rp-text)"); property("font-weight", "800") } }) { Text("${absence.startDate} – ${absence.endDate}") }
                }

                absence.note?.let { note ->
                    if (note.isNotBlank()) {
                        Div({ style { property("border-top", "1px solid var(--rp-hl-med)"); property("padding-top", "0.5rem"); property("font-size", "0.82rem") } }) {
                            Span({ style { property("color", "var(--rp-subtle)"); property("font-weight", "600"); property("display", "block"); property("margin-bottom", "0.2rem") } }) { Text("Meddelande / Anledning:") }
                            Span({ style { property("color", "var(--rp-text)"); property("font-style", "italic") } }) { Text(note) }
                        }
                    }
                }
            }

            Div({ style { property("display", "flex"); property("gap", "0.6rem"); property("justify-content", "flex-end") } }) {
                Button({
                    onClick { onClose() }
                    style {
                        property("padding", "0.55rem 1rem"); property("border-radius", "10px"); property("border", "1px solid var(--rp-hl-med)")
                        property("background", "var(--rp-surface)"); property("color", "var(--rp-subtle)"); property("font-weight", "700"); property("font-size", "0.85rem"); property("cursor", "pointer")
                    }
                }) { Text("Stäng") }

                if (isManager && onApprove != null && onReject != null) {
                    if (absence.status.lowercase() != "rejected") {
                        Button({
                            if (loading) disabled()
                            onClick {
                                loading = true
                                onReject()
                            }
                            style {
                                property("padding", "0.55rem 1rem"); property("border-radius", "10px"); property("border", "none")
                                property("background", "var(--rp-err-bg)"); property("color", "var(--rp-love)"); property("font-weight", "700"); property("font-size", "0.85rem"); property("cursor", "pointer")
                            }
                        }) { Text("Avslå") }
                    }
                    if (absence.status.lowercase() != "approved") {
                        Button({
                            if (loading) disabled()
                            onClick {
                                loading = true
                                onApprove()
                            }
                            style {
                                property("padding", "0.55rem 1.1rem"); property("border-radius", "10px"); property("border", "none")
                                property("background", "var(--rp-pine)"); property("color", "var(--rp-surface)"); property("font-weight", "700"); property("font-size", "0.85rem"); property("cursor", "pointer")
                            }
                        }) { Text("Godkänn") }
                    }
                }

                if (!isManager && absence.status.lowercase() == "pending" && onDelete != null) {
                    Button({
                        if (loading) disabled()
                        onClick {
                            loading = true
                            onDelete()
                        }
                        style {
                            property("padding", "0.55rem 1rem"); property("border-radius", "10px"); property("border", "none")
                            property("background", "var(--rp-love)"); property("color", "var(--rp-surface)"); property("font-weight", "700"); property("font-size", "0.85rem"); property("cursor", "pointer")
                        }
                    }) { Text("Ta bort") }
                }
            }
        }
    }
}

@Composable
internal fun CreateAbsenceModal(
    staff: List<se.mittpass.shared.api.StaffMemberDto>,
    currentUserId: Long?,
    isManager: Boolean,
    onConfirm: (type: String, startDate: String, endDate: String, note: String?, userId: Long?, status: String) -> Unit,
    onCancel: () -> Unit
) {
    var selectedUserId by remember { mutableStateOf(if (isManager) staff.firstOrNull()?.userId else currentUserId) }
    var type by remember { mutableStateOf("vacation") }
    var startDate by remember { mutableStateOf(todayLocalDate()) }
    var endDate by remember { mutableStateOf(todayLocalDate()) }
    var note by remember { mutableStateOf("") }
    var status by remember { mutableStateOf(if (isManager) "approved" else "pending") }
    var loading by remember { mutableStateOf(false) }

    Div({
        onClick { onCancel() }
        style {
            property("position", "fixed"); property("inset", "0")
            property("background", "rgba(13,13,24,0.4)"); property("z-index", "250")
            property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
            property("backdrop-filter", "blur(3px)"); property("padding", "1rem")
        }
    }) {
        Div({
            onClick { it.stopPropagation() }
            style {
                property("background", "var(--rp-surface)"); property("border-radius", "20px")
                property("padding", "1.75rem 1.85rem"); property("width", "450px")
                property("max-width", "100%"); property("box-shadow", "0 24px 60px rgba(13,13,24,0.25)")
            }
        }) {
            H3({
                style {
                    property("margin", "0 0 0.25rem 0"); property("font-size", "1.25rem")
                    property("font-weight", "800"); property("color", "var(--rp-text)")
                }
            }) {
                Text(if (isManager) "Registrera frånvaro / semester" else "Ansök om semester / frånvaro")
            }
            P({
                style {
                    property("margin", "0 0 1.25rem 0"); property("font-size", "0.85rem")
                    property("color", "var(--rp-subtle)"); property("font-weight", "500")
                }
            }) {
                Text("Välj period och typ av frånvaro")
            }

            // Employee selector for manager
            if (isManager) {
                Div({ style { property("margin-bottom", "1rem") } }) {
                    Span({ style { schemaModalLabel() } }) { Text("Anställd") }
                    Select({
                        onInput { evt ->
                            val v = evt.value
                            selectedUserId = v?.toLongOrNull()
                        }
                        style {
                            property("width", "100%"); property("padding", "0.6rem 0.75rem"); property("border-radius", "10px")
                            property("border", "1px solid var(--rp-hl-med)"); property("font-size", "0.9rem"); property("font-family", "inherit")
                        }
                    }) {
                        staff.forEach { member ->
                            val name = "${member.firstName ?: ""} ${member.lastName ?: ""}".ifEmpty { member.email }
                            Option(member.userId.toString(), {
                                if (member.userId == selectedUserId) selected()
                            }) {
                                Text(name)
                            }
                        }
                    }
                }
            }

            // Absence Type
            Div({ style { property("margin-bottom", "1rem") } }) {
                Span({ style { schemaModalLabel() } }) { Text("Typ av frånvaro") }
                Select({
                    onInput { evt ->
                        evt.value?.let { type = it }
                    }
                    style {
                        property("width", "100%"); property("padding", "0.6rem 0.75rem"); property("border-radius", "10px")
                        property("border", "1px solid var(--rp-hl-med)"); property("font-size", "0.9rem"); property("font-family", "inherit")
                    }
                }) {
                    Option("vacation", { if (type == "vacation") selected() }) { Text("Semester") }
                    Option("sick", { if (type == "sick") selected() }) { Text("Sjukanmälan") }
                    Option("vab", { if (type == "vab") selected() }) { Text("VAB") }
                    Option("other", { if (type == "other") selected() }) { Text("Övrig frånvaro / Tjänstledig") }
                }
            }

            // Date Range
            Div({ style { property("display", "grid"); property("grid-template-columns", "1fr 1fr"); property("gap", "0.75rem"); property("margin-bottom", "1rem") } }) {
                Div {
                    Span({ style { schemaModalLabel() } }) { Text("Startdatum") }
                    Input(type = InputType.Date) {
                        value(startDate)
                        onInput { evt -> evt.value?.let { startDate = it } }
                        style {
                            property("width", "100%"); property("padding", "0.55rem 0.65rem"); property("border-radius", "10px")
                            property("border", "1px solid var(--rp-hl-med)"); property("font-size", "0.85rem"); property("font-family", "inherit")
                        }
                    }
                }
                Div {
                    Span({ style { schemaModalLabel() } }) { Text("Slutdatum") }
                    Input(type = InputType.Date) {
                        value(endDate)
                        onInput { evt -> evt.value?.let { endDate = it } }
                        style {
                            property("width", "100%"); property("padding", "0.55rem 0.65rem"); property("border-radius", "10px")
                            property("border", "1px solid var(--rp-hl-med)"); property("font-size", "0.85rem"); property("font-family", "inherit")
                        }
                    }
                }
            }

            // Note / Reason
            Div({ style { property("margin-bottom", "1.25rem") } }) {
                Span({ style { schemaModalLabel() } }) { Text("Meddelande / Anledning (Valfritt)") }
                TextArea {
                    value(note)
                    onInput { evt -> evt.value?.let { note = it } }
                    style {
                        property("width", "100%"); property("padding", "0.6rem 0.75rem"); property("border-radius", "10px")
                        property("border", "1px solid var(--rp-hl-med)"); property("font-size", "0.85rem"); property("font-family", "inherit")
                        property("height", "65px"); property("resize", "none")
                    }
                }
            }

            // Actions
            Div({ style { property("display", "flex"); property("gap", "0.6rem"); property("justify-content", "flex-end") } }) {
                Button({
                    onClick { onCancel() }
                    style {
                        property("padding", "0.6rem 1.1rem"); property("border-radius", "10px"); property("border", "1px solid var(--rp-hl-med)")
                        property("background", "var(--rp-surface)"); property("color", "var(--rp-subtle)"); property("font-weight", "700"); property("font-size", "0.875rem"); property("cursor", "pointer")
                    }
                }) { Text("Avbryt") }

                Button({
                    if (loading) disabled()
                    onClick {
                        if (startDate <= endDate) {
                            loading = true
                            onConfirm(type, startDate, endDate, note.ifBlank { null }, selectedUserId, status)
                        }
                    }
                    style {
                        property("padding", "0.6rem 1.35rem"); property("border-radius", "10px"); property("border", "none")
                        property("background", "var(--rp-iris)"); property("color", "var(--rp-surface)"); property("font-weight", "700"); property("font-size", "0.875rem"); property("cursor", "pointer")
                        property("box-shadow", "0 2px 6px rgba(124,58,237,0.3)")
                    }
                }) { Text(if (isManager) "Spara frånvaro" else "Skicka ansökan") }
            }
        }
    }
}
